/**
 * Числа, даты и единицы по языку интерфейса (ADR-0019).
 *
 * Число и дату форматирует `Intl` с локалью интерфейса: разделители,
 * названия месяцев и порядок частей — забота CLDR, а не наших строк. Единицы
 * (га, т, м³) — НЕ `Intl`: у `Intl.NumberFormat` нет тонны и кубометра, а
 * гектар в `uz-Cyrl` CLDR печатает латиницей («1 ha»). Поэтому подпись
 * единицы — строка каталога (`units.*`), переводимая и вычитываемая как
 * любая другая, а число рядом — `Intl` той же локали.
 *
 * Пропуск — «—», а не 0: отсутствие данных нулём не рисуется (DESIGN.md).
 */
import type { Locale } from './locales';

export const MISSING = '—';
/** Неразрывный пробел между числом и единицей: «12,5 га» не рвётся строкой. */
const NBSP = '\u00a0';

export type Unit = 'ha' | 't' | 'm3';

export interface NumberOptions {
  /** Знаков после запятой (ровно столько: точность честная, не «до»). */
  digits?: number;
}

// Числа всех языков — шаблоном ru-RU: у узбекского CLDR тот же формат («8 107,5»),
// а данные `uz` есть не в каждом браузере (журнал №1114).
const cache = new Map<string, Intl.NumberFormat>();
function numberFormat(locale: Locale, digits: number): Intl.NumberFormat {
  const key = `${locale}|${digits}`;
  let format = cache.get(key);
  if (!format) {
    format = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    cache.set(key, format);
  }
  return format;
}

const known = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

/** Число по правилам языка: «12 345,5». */
export function formatNumber(locale: Locale, value: number | null | undefined, { digits = 0 }: NumberOptions = {}): string {
  return known(value) ? numberFormat(locale, digits).format(value) : MISSING;
}

/** Точность по умолчанию: гектары — до сотых, тонны — до десятых, вода — целые. */
export const UNIT_DIGITS: Record<Unit, number> = { ha: 2, t: 1, m3: 0 };

/**
 * Число с единицей: «46 738,78 га», «46 738,78 ga». `label` — подпись единицы
 * из каталога текущего языка (`units.ha` и т. д.).
 */
export function formatUnit(
  locale: Locale,
  value: number | null | undefined,
  unit: Unit,
  label: string,
  { digits = UNIT_DIGITS[unit] }: NumberOptions = {},
): string {
  if (!known(value)) return MISSING;
  return `${numberFormat(locale, digits).format(value)}${NBSP}${label}`;
}

/** Процент от доли 0–100 (не 0–1): так считают экраны. Знак — по CLDR языка. */
export function formatPercent(_locale: Locale, value: number | null | undefined, { digits = 0 }: NumberOptions = {}): string {
  if (!known(value)) return MISSING;
  return new Intl.NumberFormat('ru-RU', {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value / 100);
}

/**
 * Дата календаря (`YYYY-MM-DD`) без сдвига часового пояса: строка разбирается
 * как местная дата, а не как полночь UTC, иначе в Ташкенте (UTC+5) она
 * оставалась бы прежней, а западнее — уезжала на день назад.
 */
export function formatDate(
  locale: Locale,
  value: string | Date | null | undefined,
  style: DateStyle = 'long',
): string {
  if (!value) return MISSING;
  let date: Date;
  if (value instanceof Date) date = value;
  else {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return MISSING;
    date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  if (Number.isNaN(date.getTime())) return MISSING;
  return new Intl.DateTimeFormat(locale, dateOptions(locale, style)).format(date);
}

export type DateStyle = 'day' | 'short' | 'long';

/** «26 авг.» (окно внутри сезона), «26 авг. 2026 г.», «26 августа 2026 г.» — по CLDR языка. */
const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  day: { day: 'numeric', month: 'short' },
  short: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
};

/**
 * Параметры даты по языку. По-узбекски месяц в датах `day` и `short` — ПОЛНЫЙ: к дате
 * слитно клеятся окончания (`{date}gacha`, `{date}dan`, `{date}da`, `{date}ga`), и с
 * сокращением выходило «25-noygacha» / «25-нойгача» — слово, которого нет. Полное
 * имя даёт «25-noyabrgacha». Приложение берёт узбекскую дату не отсюда, а из каталога
 * месяцев (`useFormat().date`, `units.month.inDate` — тот же принцип); пресеты держат
 * правило и для прямых вызовов `formatDate`.
 */
function dateOptions(locale: Locale, style: DateStyle): Intl.DateTimeFormatOptions {
  const options = DATE_STYLES[style];
  return locale !== 'ru' && options.month === 'short' ? { ...options, month: 'long' } : options;
}
